import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import SectionTabs from '../../components/SectionTabs';
import { Avatar, KpiCard, LinkButton, SectionLabel, StatusPill } from '../../components/ui/dg';
import {
  BalancesOverview, FeeCode, FeeRule, FeesDueSettings, OwnerBalance, OwnerEarnings, PlatformFees, RecentSettlement,
  getBalances, getFees, getFeesDueSettings, getOwnerDetail, recordSettlement, updateFees, updateFeesDueSettings
} from '../../api/services/feesService';

/**
 * Section Frais (administrateurs), en trois onglets :
 *  - « Soldes » (`?vue=soldes`, par défaut) : ce que doit chaque propriétaire
 *    (frais encaissés + commission sur ses trajets terminés), ce qu'il a réglé,
 *    l'ajout d'un règlement reçu, et le résumé des frais et des relances ;
 *  - « Barème » (`?vue=taux`) : les frais Dingo appliqués aux NOUVELLES
 *    locations (service et protection payés par le locataire, commission) ;
 *  - « Relances » (`?vue=configuration`) : les seuils des rappels et de la
 *    mise hors ligne pour impayé.
 * Dingo n'encaisse rien en ligne : le propriétaire reçoit tout en main propre
 * et reverse sa part.
 */

const card = 'rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]';
const cardTitle = 'text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]';
const cardHint = 'mt-1.5 text-[12.5px] leading-[1.45] text-[#6B7280]';
const input = 'h-10 w-24 rounded-xl border border-[#E5E7EB] px-3 text-[13.5px] tabular-nums focus:border-[#C7C4F6] focus:outline-none focus:ring-4 focus:ring-[#4F46E5]/10';

const FEE_META: { code: FeeCode; label: string; who: string; percentOnly?: boolean }[] = [
  { code: 'renter_service', label: 'Frais de service', who: 'Payés par le locataire, en plus du loyer' },
  { code: 'renter_protection', label: 'Frais de protection', who: 'Payés par le locataire, en plus du loyer' },
  { code: 'owner_commission', label: 'Commission', who: 'Prise sur le loyer du propriétaire' },
  {
    code: 'owner_fee_share',
    label: 'Part du propriétaire sur les frais',
    who: 'Pourcentage des frais locataires (service et protection) qu’il garde, déduit de ce qu’il reverse',
    percentOnly: true
  }
];

const ZERO: PlatformFees = {
  renter_service: { percent: 0, min_amount: 0 },
  renter_protection: { percent: 0, min_amount: 0 },
  owner_commission: { percent: 0, min_amount: 0 },
  owner_fee_share: { percent: 0, min_amount: 0 }
};

const dt = (value: number) => `${(Math.round(value * 1000) / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 3 })} DT`;
const feeFor = (rule: FeeRule, price: number) => {
  if (rule.percent <= 0) return rule.min_amount;
  return Math.max((price * rule.percent) / 100, rule.min_amount);
};
const isFree = (rule: FeeRule) => rule.percent <= 0 && rule.min_amount <= 0;
const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

// --- Taux -------------------------------------------------------------------------------

const FeesEditor = ({ token }: { token: string }) => {
  const [saved, setSaved] = useState<PlatformFees>(ZERO);
  const [draft, setDraft] = useState<PlatformFees>(ZERO);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [example, setExample] = useState(20);

  useEffect(() => {
    getFees(token)
      // Une API sans owner_fee_share (avant la migration 087) laisse la part à 0.
      .then((fees) => { const all = { ...ZERO, ...fees }; setSaved(all); setDraft(all); })
      .catch((error) => setMessage({ tone: 'error', text: error.message }))
      .finally(() => setLoading(false));
  }, [token]);

  const dirty = JSON.stringify(saved) !== JSON.stringify(draft);
  const setRule = (code: FeeCode, field: keyof FeeRule, value: string) => {
    const number = Number(value.replace(',', '.'));
    setDraft((current) => ({ ...current, [code]: { ...current[code], [field]: Number.isFinite(number) ? number : 0 } }));
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const fees = { ...ZERO, ...(await updateFees(token, draft)) };
      setSaved(fees);
      setDraft(fees);
      setMessage({ tone: 'ok', text: 'Enregistré : s’applique aux locations demandées à partir de maintenant.' });
    } catch (error: any) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const service = feeFor(draft.renter_service, example);
  const protection = feeFor(draft.renter_protection, example);
  const commission = feeFor(draft.owner_commission, example);
  const share = ((service + protection) * Math.max(draft.owner_fee_share.percent, 0)) / 100;

  return (
    <section className={`${card} p-5`}>
      <h2 className={cardTitle}>Frais en vigueur</h2>
      <p className={cardHint}>
        Chaque frais vaut un pourcentage du loyer, avec un minimum en DT. À 0 et 0, il ne coûte rien et l’app l’affiche barré.
        Les taux sont figés sur chaque location à sa demande : les locations existantes ne changent pas.
      </p>
      {loading ? (
        <div className="mt-4 h-28 animate-pulse rounded-xl bg-[#EEF0F5]" />
      ) : (
        <div className="mt-4 divide-y divide-gray-100">
          {FEE_META.map(({ code, label, who, percentOnly }) => (
            <div key={code} className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3">
              <div className="min-w-[200px] flex-1">
                <div className={`text-[13px] font-medium ${!percentOnly && isFree(draft[code]) ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{label}</div>
                <div className="text-[12px] text-gray-500">{who}</div>
              </div>
              <label className="flex items-center gap-2 text-[12px] text-gray-500">
                <input className={input} inputMode="decimal" value={draft[code].percent} onChange={(e) => setRule(code, 'percent', e.target.value)} />
                %
              </label>
              {percentOnly ? (
                <span className="w-[136px]" />
              ) : (
                <label className="flex items-center gap-2 text-[12px] text-gray-500">
                  min.
                  <input className={input} inputMode="decimal" value={draft[code].min_amount} onChange={(e) => setRule(code, 'min_amount', e.target.value)} />
                  DT
                </label>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 rounded-2xl bg-[#F7F7FB] px-4 py-3 text-[13px] leading-[1.6] text-[#4B5563]">
        Pour une location de{' '}
        <input className={`${input} w-16`} inputMode="decimal" value={example} onChange={(e) => setExample(Number(e.target.value.replace(',', '.')) || 0)} />{' '}
        DT : le locataire paie <b className="tabular-nums">{dt(example + service + protection)}</b>, le propriétaire garde{' '}
        <b className="tabular-nums">{dt(example - commission + share)}</b>
        {share > 0 && <> (dont <span className="tabular-nums">{dt(share)}</span> de sa part des frais)</>} et reverse{' '}
        <b className="tabular-nums">{dt(service + protection + commission - share)}</b> à Dingo.
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={save}
          className="h-12 rounded-3xl bg-[#4F46E5] px-6 text-[15px] font-bold text-white hover:bg-[#4338CA] disabled:opacity-40"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer les frais'}
        </button>
        {dirty && <button type="button" onClick={() => setDraft(saved)} className="text-[13px] text-gray-500 hover:text-gray-800">Annuler</button>}
        {message && <span className={`text-[12px] ${message.tone === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{message.text}</span>}
      </div>
    </section>
  );
};

// --- Soldes des propriétaires --------------------------------------------------------------

const SettlementForm = ({ onSubmit }: { onSubmit: (amount: number, note: string) => Promise<void> }) => {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const value = Number(amount.replace(',', '.'));
  return (
    <form
      className="flex flex-wrap items-center gap-2"
      onSubmit={async (event) => {
        event.preventDefault();
        if (!(value > 0)) return;
        setBusy(true);
        try { await onSubmit(value, note); setAmount(''); setNote(''); } finally { setBusy(false); }
      }}
    >
      <input className={input} inputMode="decimal" placeholder="Montant" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <input className={`${input} w-64`} placeholder="Note (espèces, virement…)" value={note} onChange={(e) => setNote(e.target.value)} />
      <button type="submit" disabled={!(value > 0) || busy} className="h-10 rounded-[20px] bg-[#4F46E5] px-4 text-[13.5px] font-bold text-white hover:bg-[#4338CA] disabled:opacity-40">
        Enregistrer le règlement
      </button>
    </form>
  );
};

const OwnerDetail = ({ token, owner, onChanged }: { token: string; owner: OwnerBalance; onChanged: () => void }) => {
  const [detail, setDetail] = useState<OwnerEarnings | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    getOwnerDetail(token, owner.owner_id).then(setDetail).catch((e) => setError(e.message));
  }, [token, owner.owner_id]);

  if (error) return <div className="px-4 py-3 text-[12px] text-red-700">{error}</div>;
  if (!detail) return <div className="mx-4 my-3 h-16 animate-pulse rounded-xl bg-gray-100" />;
  return (
    <div className="space-y-3 bg-[#F7F7FB] px-5 py-4">
      <SettlementForm
        onSubmit={async (amount, note) => {
          const updated = await recordSettlement(token, owner.owner_id, amount, note);
          setDetail(updated);
          onChanged();
        }}
      />
      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">Trajets terminés</div>
          <ul className="divide-y divide-[#EEF0F4] rounded-2xl bg-white">
            {detail.rentals.length === 0 && <li className="px-3 py-2 text-[12px] text-gray-400">Aucun</li>}
            {detail.rentals.map((line) => (
              <li key={line.id_rental} className="flex items-center justify-between gap-3 px-3 py-2 text-[12px]">
                <span className="min-w-0 truncate text-gray-700">{date(line.completed_at)} · {line.vehicle_label || '—'} · {line.renter_name || '—'}</span>
                <span className="shrink-0 tabular-nums text-gray-500">loyer {dt(line.rental_price)} · dû <b className="text-gray-900">{dt(line.owed)}</b></span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.4px] text-[#6B7280]">Règlements</div>
          <ul className="divide-y divide-[#EEF0F4] rounded-2xl bg-white">
            {detail.settlements.length === 0 && <li className="px-3 py-2 text-[12px] text-gray-400">Aucun</li>}
            {detail.settlements.map((s) => (
              <li key={s.id_settlement} className="flex items-center justify-between gap-3 px-3 py-2 text-[12px]">
                <span className="min-w-0 truncate text-gray-700">{date(s.created_at)}{s.note ? ` · ${s.note}` : ''}</span>
                <b className="shrink-0 tabular-nums text-gray-900">{dt(s.amount)}</b>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const shortName = (name: string) => {
  const [first, ...rest] = name.trim().split(/\s+/);
  return rest.length ? `${first} ${rest[rest.length - 1][0]}.` : first || '—';
};
const photoUrl = (url?: string | null) => (!url ? null : /^https?:\/\//.test(url) ? url : `${process.env.REACT_APP_API_URL || ''}${url.replace(/^\//, '')}`);
const TONES = ['green', 'orange', 'grey', 'indigo'] as const;
const toneOf = (id: string) => TONES[id.split('').reduce((sum, c) => sum + c.charCodeAt(0), 0) % TONES.length];
const previousMonthName = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toLocaleDateString('fr-FR', { month: 'long' });
};
const settlementLine = (s: RecentSettlement) => {
  const date = new Date(s.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const note = s.note?.trim();
  const by = s.recorded_by_name ? `, reçus par ${s.recorded_by_name}` : '';
  return note ? `${note}${by} le ${date}` : `Le ${date}${by}`;
};

const Balances = ({ token, onEdit }: { token: string; onEdit: (view: 'taux' | 'configuration') => void }) => {
  const [data, setData] = useState<BalancesOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [fees, setFees] = useState<PlatformFees | null>(null);
  const [settings, setSettings] = useState<FeesDueSettings | null>(null);

  const load = useCallback(() => {
    getBalances(token).then(setData).catch((e) => setError(e.message));
  }, [token]);
  useEffect(load, [load]);
  useEffect(() => {
    getFees(token).then((f) => setFees({ ...ZERO, ...f })).catch(() => {});
    getFeesDueSettings(token).then(setSettings).catch(() => {});
  }, [token]);

  const owners = data?.owners ?? null;
  const dueCount = (owners || []).filter((o) => o.balance > 0).length;
  const totalDue = (owners || []).reduce((sum, o) => sum + Math.max(o.balance, 0), 0);
  const month = data?.month;
  const trend = month && month.settled_previous > 0
    ? Math.round(((month.settled - month.settled_previous) / month.settled_previous) * 100)
    : null;
  const COLS = 'grid-cols-[1.75fr_0.55fr_0.8fr_0.75fr_0.75fr_0.85fr_210px]';

  const rule = (r: FeeRule, percentOnly = false) =>
    isFree(r) ? 'Offerts'
      : percentOnly ? `${r.percent} % des frais locataire`
      : `${r.percent} %${r.min_amount > 0 ? `, ${r.min_amount} DT minimum` : ''}`;

  return (
    <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,2.1fr)_minmax(320px,1fr)]">
      <div className="min-w-0 space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <KpiCard highlight label="Reste à percevoir" value={owners ? dt(totalDue) : '…'} valueColor="#B91C1C" subColor="#6B7280"
            sub={owners ? `${dueCount} propriétaire${dueCount > 1 ? 's' : ''}` : ''} />
          <KpiCard label="Réglé ce mois" value={month ? dt(month.settled) : '…'} valueColor="#047857" subColor="#6B7280"
            sub={trend === null ? 'règlements reçus' : `${trend > 0 ? '+' : ''}${trend} % vs ${previousMonthName()}`} />
          <KpiCard label="Trajets terminés" value={month ? String(month.completed_rentals) : '…'} sub="ce mois" />
        </div>

        <section className={`${card} overflow-hidden`}>
          <div className="px-7 pb-1 pt-5"><span className={cardTitle}>Ce que doivent les propriétaires</span></div>
          {error && <div className="px-7 pb-4 text-[12.5px] text-[#B91C1C]">{error}</div>}
          {!owners && !error && <div className="mx-7 mb-6 h-24 animate-pulse rounded-xl bg-[#EEF0F5]" />}
          {owners && (
            <div className="overflow-x-auto">
              <div className="min-w-[720px]">
                <div className={`grid ${COLS} gap-3 px-7 pb-2.5 pt-3`}>
                  {['Propriétaire', 'Trajets', 'Dû', 'Réglé', 'Solde', 'État'].map((h) => <SectionLabel key={h}>{h}</SectionLabel>)}
                  <span />
                </div>
                {owners.length === 0 && <p className="border-t border-[#EEF0F4] px-7 py-8 text-center text-[13px] text-[#6B7280]">Aucun frais dû pour le moment.</p>}
                {owners.map((owner) => {
                  const state = owner.blocked ? { label: 'Hors ligne', tone: 'red' as const }
                    : owner.overdue ? { label: 'À relancer', tone: 'amber' as const }
                    : owner.balance > 0 ? { label: 'À régler', tone: 'grey' as const }
                    : { label: 'À jour', tone: 'green' as const };
                  const isOpen = open === owner.owner_id;
                  return (
                    <div key={owner.owner_id} className="border-t border-[#EEF0F4]">
                      <div className={`grid ${COLS} cursor-pointer items-center gap-3 px-7 py-3 hover:bg-[#FAFAFD]`}
                        onClick={() => setOpen(isOpen ? null : owner.owner_id)}>
                        <span className="flex min-w-0 items-center gap-3">
                          <Avatar name={owner.name} photo={photoUrl(owner.profile_photo)} size={32} tone={toneOf(owner.owner_id)} />
                          <b className="truncate text-[14px] font-bold text-[#1E293B]" title={owner.name}>{shortName(owner.name)}</b>
                        </span>
                        <span className="text-[13.5px] tabular-nums text-[#1E293B]">{owner.rentals_count}</span>
                        <span className="text-[13.5px] tabular-nums text-[#1E293B]">{dt(owner.owed)}</span>
                        <span className="text-[13.5px] tabular-nums text-[#6B7280]">{dt(owner.settled)}</span>
                        <b className={`text-[14px] font-extrabold tabular-nums ${owner.balance > 0 ? 'text-[#B91C1C]' : 'text-[#1E293B]'}`}>{dt(Math.max(owner.balance, 0))}</b>
                        <span><StatusPill tone={state.tone}>{state.label}</StatusPill></span>
                        <span className="justify-self-end">
                          {owner.balance > 0 && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); setOpen(isOpen ? null : owner.owner_id); }}
                              className={`h-9 whitespace-nowrap rounded-full border px-4 text-[13px] font-bold transition-colors ${isOpen ? 'border-[#1E293B] bg-[#1E293B] text-white' : 'border-[#E5E7EB] bg-white text-[#1E293B] hover:bg-gray-50'}`}>
                              Enregistrer un règlement
                            </button>
                          )}
                        </span>
                      </div>
                      {isOpen && <OwnerDetail token={token} owner={owner} onChanged={load} />}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {data?.recent && data.recent.length > 0 && (
          <section className={`${card} px-7 pb-3 pt-5`}>
            <span className={cardTitle}>Derniers règlements</span>
            <div className="mt-3">
              {data.recent.map((s, i) => (
                <div key={s.id_settlement} className="flex items-center gap-3 py-3" style={{ borderTop: i ? '1px solid #EEF0F4' : 'none' }}>
                  <Avatar name={s.name} photo={photoUrl(s.profile_photo)} size={32} tone={toneOf(s.owner_id)} />
                  <span className="min-w-0 flex-1">
                    <b className="block truncate text-[14px] font-bold text-[#1E293B]">{shortName(s.name)}</b>
                    <span className="block truncate text-[12.5px] text-[#6B7280]">{settlementLine(s)}</span>
                  </span>
                  <b className="text-[14px] font-extrabold tabular-nums text-[#047857]">+{dt(s.amount)}</b>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Le barème et les relances, résumés ; on les change dans leurs onglets. */}
      <section className={`${card} self-start overflow-hidden`}>
        <div className="flex items-center justify-between px-7 pb-1 pt-5">
          <span className={cardTitle}>Frais en vigueur</span>
          <LinkButton onClick={() => onEdit('taux')}>Modifier</LinkButton>
        </div>
        {!fees ? <div className="mx-7 my-3 h-24 animate-pulse rounded-xl bg-[#EEF0F5]" /> : FEE_META.map(({ code, label, percentOnly }, i) => (
          <div key={code} className="mx-7 flex items-center gap-3 py-3" style={{ borderTop: i ? '1px solid #EEF0F4' : 'none' }}>
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-bold text-[#1E293B]">{percentOnly ? 'Part du propriétaire' : label}</span>
              <span className="block text-[12.5px] text-[#6B7280]">{code.startsWith('renter') ? 'Locataire' : 'Propriétaire'}</span>
            </span>
            <b className={`text-right text-[13.5px] font-bold ${isFree(fees[code]) ? 'text-[#047857]' : 'text-[#1E293B]'}`}>{rule(fees[code], percentOnly)}</b>
          </div>
        ))}
        <div className="mt-3 h-2 bg-[#F1F2F8]" />
        <div className="flex items-center justify-between px-7 pb-1 pt-5">
          <span className={cardTitle}>Rappels et mise hors ligne</span>
          <LinkButton onClick={() => onEdit('configuration')}>Modifier</LinkButton>
        </div>
        {!settings ? <div className="mx-7 my-3 h-16 animate-pulse rounded-xl bg-[#EEF0F5]" /> : (
          <div className="pb-4">
            <div className="mx-7 py-3"><b className="block text-[14px] font-bold">Rappel</b><span className="text-[12.5px] text-[#6B7280]">À partir de {settings.remindThreshold} DT, tous les {settings.remindEveryDays} jours</span></div>
            <div className="mx-7 border-t border-[#EEF0F4] py-3"><b className="block text-[14px] font-bold">Hors ligne</b><span className="text-[12.5px] text-[#6B7280]">À partir de {settings.blockAmount} DT, après {settings.blockAfterDays} jours</span></div>
          </div>
        )}
      </section>
    </div>
  );
};

// --- Configuration ------------------------------------------------------------------------

const SETTING_FIELDS: { key: keyof FeesDueSettings; label: string; hint: string; unit: string }[] = [
  { key: 'remindThreshold', label: 'Seuil de rappel', hint: 'Solde à partir duquel le propriétaire reçoit des rappels', unit: 'DT' },
  { key: 'remindEveryDays', label: 'Fréquence des rappels', hint: 'Un rappel tous les…', unit: 'jours' },
  { key: 'overdueDays', label: 'À relancer après', hint: 'Au-dessus du seuil depuis… : pastille et « À relancer » ici', unit: 'jours' },
  { key: 'blockAmount', label: 'Hors ligne à partir de', hint: 'Solde qui retire les annonces du catalogue', unit: 'DT' },
  { key: 'blockAfterDays', label: 'Hors ligne après', hint: 'Au-dessus du seuil depuis… : annonces retirées', unit: 'jours' }
];

const FeesDueConfig = ({ token }: { token: string }) => {
  const [saved, setSaved] = useState<FeesDueSettings | null>(null);
  const [draft, setDraft] = useState<FeesDueSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    getFeesDueSettings(token)
      .then((settings) => { setSaved(settings); setDraft(settings); })
      .catch((error) => setMessage({ tone: 'error', text: error.message }));
  }, [token]);

  const dirty = saved && draft && JSON.stringify(saved) !== JSON.stringify(draft);
  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setMessage(null);
    try {
      const settings = await updateFeesDueSettings(token, draft);
      setSaved(settings);
      setDraft(settings);
      setMessage({ tone: 'ok', text: 'Enregistré : appliqué tout de suite, blocages et déblocages compris.' });
    } catch (error: any) {
      setMessage({ tone: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={`${card} p-5`}>
      <h2 className={cardTitle}>Rappels et mise hors ligne</h2>
      <p className={cardHint}>
        Ce qui arrive à un propriétaire qui ne règle pas ce qu’il doit. Ses annonces reviennent en ligne dès qu’un
        règlement fait repasser son solde sous le seuil de rappel.
      </p>
      {!draft ? (
        <div className="mt-4 h-40 animate-pulse rounded-xl bg-gray-100" />
      ) : (
        <div className="mt-4 divide-y divide-gray-100">
          {SETTING_FIELDS.map(({ key, label, hint, unit }) => (
            <div key={key} className="flex flex-wrap items-center gap-x-6 gap-y-2 py-3">
              <div className="min-w-[220px] flex-1">
                <div className="text-[13px] font-medium text-gray-900">{label}</div>
                <div className="text-[12px] text-gray-500">{hint}</div>
              </div>
              <label className="flex items-center gap-2 text-[12px] text-gray-500">
                <input
                  className={input}
                  inputMode="decimal"
                  value={draft[key]}
                  onChange={(e) => {
                    const value = Number(e.target.value.replace(',', '.'));
                    setDraft({ ...draft, [key]: Number.isFinite(value) ? value : 0 });
                  }}
                />
                {unit}
              </label>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          disabled={!dirty || saving}
          onClick={save}
          className="h-12 rounded-3xl bg-[#4F46E5] px-6 text-[15px] font-bold text-white hover:bg-[#4338CA] disabled:opacity-40"
        >
          {saving ? 'Enregistrement…' : 'Enregistrer la configuration'}
        </button>
        {dirty && <button type="button" onClick={() => setDraft(saved)} className="text-[13px] text-gray-500 hover:text-gray-800">Annuler</button>}
        {message && <span className={`text-[12px] ${message.tone === 'ok' ? 'text-green-700' : 'text-red-700'}`}>{message.text}</span>}
      </div>
    </section>
  );
};

const TabPage = ({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) => (
  <div className="flex-1 overflow-y-auto">
    <div className={`${wide ? '' : 'max-w-4xl'} space-y-5 px-4 pb-6 pt-1 lg:px-7`}>{children}</div>
  </div>
);

const ManagementFees = () => {
  const { token } = useAuth();
  const [params, setParams] = useSearchParams();
  if (!token) return null;
  const goTo = (view: string) => { const next = new URLSearchParams(params); next.set('vue', view); setParams(next, { replace: true }); };
  return (
    <SectionTabs
      ariaLabel="Vue des frais"
      views={[
        { id: 'soldes', label: 'Soldes', render: () => <TabPage wide><Balances token={token} onEdit={goTo} /></TabPage> },
        { id: 'taux', label: 'Barème', render: () => <TabPage><FeesEditor token={token} /></TabPage> },
        { id: 'configuration', label: 'Relances', render: () => <TabPage><FeesDueConfig token={token} /></TabPage> }
      ]}
    />
  );
};

export default ManagementFees;
